/**
 * The offerings piece's behavior half: reads the soonest offerings (instants,
 * the same for every visitor), words each one's day and time in the visitor's
 * time zone with its length, few spots and price, and opens an offering's
 * page. Draws nothing.
 */
import type { SiteOfferingDto } from '@telify/shared';
import { useMemo } from 'react';

import { useSoonestOfferings } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { formatDayLabel, formatDuration, formatSlotTime, getClientTimezone, priceText } from '../../format';
import { usePageMover } from '../../pageMover';
import { wholeCount } from '../../wholeCount';
import { spotsText } from '../events-row/EventCard';
import { contract, type OfferingsActions, type OfferingsEvents, type OfferingsItem, type OfferingsState } from './contract';

export type OfferingsRuntime = PieceRuntime<typeof contract, OfferingsState, OfferingsActions>;

/** An event with this many spots or fewer shows them, as the event cards do. */
const FEW_SPOTS = 3;

/** One offering worded for its visitor: the server sends instants, and the day and time are the visitor's own. */
export function toItem(o: SiteOfferingDto, tz: string): OfferingsItem {
  return {
    ...o,
    day: formatDayLabel(o.startsAt, tz),
    time: formatSlotTime(o.startsAt, tz),
    length: formatDuration(o.durationMinutes),
    spots: o.spotsLeft !== null && o.spotsLeft <= FEW_SPOTS ? spotsText(o.spotsLeft) : null,
    priceLabel: priceText(o.price),
  };
}

export function useOfferings(input: PieceInput<OfferingsEvents> = {}): OfferingsRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const timezone = useMemo(() => getClientTimezone(), []);
  const query = useSoonestOfferings(wholeCount(options.limit));

  const offerings = useMemo(() => (query.data ?? []).map((o) => toItem(o, timezone)), [query.data, timezone]);
  const status: OfferingsState['status'] = query.isError ? 'error' : query.isPending ? 'loading' : 'ready';

  const actions = useMemo<OfferingsActions>(
    () => ({
      open: (offeringId) => {
        const offering = offerings.find((o) => o.id === offeringId);
        if (!offering) return;
        emit('opened', { kind: offering.kind, offeringId });
        navigate(offering.path);
      },
    }),
    [offerings, emit, navigate],
  );

  return { state: { status, offerings, timezone }, actions, options };
}
